// ---------------------------------------------------------------------------
// Deal Analyzer good-deal verdict (Task 1618).
//
// A transparent Strong / Fair / Weak rating derived from the shared projection
// engine's key year-1 / hold metrics: going-in cap rate, cash-on-cash return,
// DSCR, and selected-hold IRR. Pure and dependency-free so the server (authoritative,
// recomputed on every save) and any client preview apply the exact same
// thresholds. Each contributing metric produces a plain-language reason so the
// rating is explainable, never a black box.
//
// Scoring: each available metric scores 0 (weak), 1 (fair), or 2 (strong)
// against the thresholds below; the verdict is the average across available
// metrics (≥1.5 strong, ≥0.75 fair, else weak). Metrics that can't be computed
// (e.g. DSCR on an all-cash deal) are simply excluded rather than penalized.
// ---------------------------------------------------------------------------

import type { AnalysisVerdict, AnalysisVerdictRating } from "./schema/dealAnalyses";
import type { ProjectionResult } from "./investment-projections";

interface MetricBand {
  key: "capRate" | "cashOnCash" | "dscr" | "irr";
  label: string;
  /** value ≥ strong → 2 points; value ≥ fair → 1 point; else 0. */
  strong: number;
  fair: number;
  format: (v: number) => string;
}

const pct = (v: number) => `${v.toFixed(1)}%`;
const ratio = (v: number) => `${v.toFixed(2)}x`;

// Thresholds reflect common commercial-underwriting rules of thumb: lenders
// typically want DSCR ≥ 1.25; investors commonly target ~8%+ cash-on-cash,
// ~6%+ going-in cap rate, and low-teens IRR over a ~5-year hold.
export const VERDICT_BANDS: readonly MetricBand[] = [
  {
    key: "capRate",
    label: "Going-in cap rate",
    strong: 6.5,
    fair: 5,
    format: pct,
  },
  { key: "cashOnCash", label: "Cash-on-cash return", strong: 8, fair: 4, format: pct },
  { key: "dscr", label: "DSCR", strong: 1.35, fair: 1.2, format: ratio },
  { key: "irr", label: "Hold-period IRR", strong: 13, fair: 8, format: pct },
] as const;

/** Pull the verdict's input metrics from a shared-engine projection result. */
export function verdictMetricsFromProjection(p: ProjectionResult): {
  capRate: number | null;
  cashOnCash: number | null;
  dscr: number | null;
  irr: number | null;
} {
  const y1 = p.years[0];
  const selectedHold =
    p.summary.selectedHoldResult ??
    p.years[Math.min(4, p.years.length - 1)];
  return {
    capRate: y1?.capRateOnPurchase ?? null,
    cashOnCash: y1?.cashOnCash ?? null,
    dscr: p.summary.allCash ? null : (y1?.dscr ?? null),
    irr: selectedHold?.irr ?? null,
  };
}

export function computeDealVerdict(metrics: {
  capRate: number | null;
  cashOnCash: number | null;
  dscr: number | null;
  irr: number | null;
}): AnalysisVerdict {
  let points = 0;
  let available = 0;
  const reasons: string[] = [];

  for (const band of VERDICT_BANDS) {
    const v = metrics[band.key];
    if (v == null || !Number.isFinite(v)) continue;
    available += 1;
    const score = v >= band.strong ? 2 : v >= band.fair ? 1 : 0;
    points += score;
    const tone =
      score === 2 ? "strong" : score === 1 ? "acceptable" : "below target";
    const target =
      score === 2
        ? ""
        : score === 1
          ? ` (strong is ${band.format(band.strong)}+)`
          : ` (looking for ${band.format(band.fair)}+)`;
    reasons.push(`${band.label} of ${band.format(v)} is ${tone}${target}.`);
  }

  if (available === 0) {
    return {
      rating: "weak",
      score: 0,
      metrics,
      reasons: [
        "Not enough inputs yet to score this deal — add income, expenses, and a purchase price.",
      ],
    };
  }

  const avg = points / available;
  const rating: AnalysisVerdictRating =
    avg >= 1.5 ? "strong" : avg >= 0.75 ? "fair" : "weak";
  return { rating, score: Math.round(avg * 100) / 100, metrics, reasons };
}
